/**
 * @namespace ThemeEvents
 * @description Une collection d'Ã©vÃ©nements spÃ©cifiques au thÃ¨me qui peuvent Ãªtre utilisÃ©s pour dÃ©clencher et Ã©couter des changements partout dans le thÃ¨me.
 * @example
 * document.dispatchEvent(new VariantUpdatedEvent(variant, sectionId, { html }));
 * document.addEventListener(ThemeEvents.variantUpdated, (e) => { console.log(e.detail.variant) });
 */

export class ThemeEvents {
  static cartUpdated = 'cart:updated'; // Quand le panier est mis Ã  jour
  static cartOpen = 'cart:open'; // Ouvre le panier
  static cartClose = 'cart:close'; // Ferme le panier
  static variantSelected = 'variant:selected'; // Quand une variante est sÃ©lectionnÃ©e
  static variantUpdated = 'variant:updated'; // Quand une variante est mise Ã  jour
  static filtersChanged = 'filters:changed'; // Quand les filtres ou le tri sont modifiÃ©s
  static filtersUpdated = 'filters:updated'; // Quand les filtres ou le tri sont mis Ã  jour
  static toggleCrossSellsUpdated = 'toggle-cross-sells:updated'; // Quand les cross-sells sont mis Ã  jour
  static quantityBreaksUpdated = 'quantity-breaks:updated'; // Quand les quantitÃ©s sont mises Ã  jour
  static subscriptionUpdated = 'subscription:updated'; // Quand l'abonnement est mis Ã  jour
  static formUpdated = 'form:updated'; // Quand le formulaire produit a fini sa mise Ã  jour
  static wishlistUpdated = 'wishlist:updated'; // Quand la wishlist est mise Ã  jour
  static wishlistItemAdded = 'wishlist:item-added'; // Quand un item est ajoutÃ© Ã  la wishlist
  static wishlistItemRemoved = 'wishlist:item-removed'; // Quand un item est retirÃ© de la wishlist
}

// Ã‰vÃ©nement dÃ©clenchÃ© quand le panier est mis Ã  jour
export class CartUpdatedEvent extends Event {
  /**
   * CrÃ©e un nouveau CartUpdatedEvent
   * @param {Object} resource - Le nouvel objet panier
   * @param {Object} [data] - DonnÃ©es supplÃ©mentaires de l'Ã©vÃ©nement
   * @param {boolean} [data.didError] - Indique si l'opÃ©ration sur le panier a Ã©chouÃ©
   * @param {string} [data.source] - La source de la mise Ã  jour du panier
   * @param {string} [data.productId] - L'identifiant de la fiche produit mise Ã  jour
   * @param {number} [data.itemCount] - Le nombre d'articles dans le panier
   * @param {string} [data.variantId] - L'identifiant de la variante du produit mise Ã  jour
   * @param {Record<string, string>} [data.sections] - Les sections affectÃ©es par l'opÃ©ration sur le panier
   */
  constructor(resource, data) {
    super(ThemeEvents.cartUpdated, { bubbles: true });
    this.detail = {
      resource,
      data: {
        ...data,
      },
    };
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© quand une variante est sÃ©lectionnÃ©e
export class VariantSelectedEvent extends Event {
  /**
   * CrÃ©e un nouveau VariantSelectedEvent
   * @param {Object} resource - Le nouvel objet variante
   * @param {string} resource.id - L'identifiant de la variante
   */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© aprÃ¨s la mise Ã  jour d'une variante
export class VariantUpdatedEvent extends Event {
  /**
   * CrÃ©e un nouveau VariantUpdatedEvent
   * @param {Object} resource - Le nouvel objet variante
   * @param {string} resource.id - L'identifiant de la variante
   * @param {boolean} resource.available - Indique si la variante est disponible
   * @param {boolean} resource.inventory_management - Indique si la variante a une gestion des stocks
   * @param {Object} [resource.featured_media] - Le mÃ©dia principal de la variante
   * @param {string} [resource.featured_media.id] - L'identifiant du mÃ©dia principal
   * @param {Object} [resource.featured_media.preview_image] - L'image d'aperÃ§u du mÃ©dia principal
   * @param {string} [resource.featured_media.preview_image.src] - L'URL de l'image d'aperÃ§u
   * @param {string} sourceId - L'identifiant de l'Ã©lÃ©ment depuis lequel l'action a Ã©tÃ© dÃ©clenchÃ©e
   * @param {Object} data - DonnÃ©es supplÃ©mentaires de l'Ã©vÃ©nement
   * @param {Document} data.html - Le nouveau fragment de document pour la variante
   * @param {string} data.productId - L'identifiant du produit de la variante mise Ã  jour, utilisÃ© pour s'assurer que le bon formulaire produit est mis Ã  jour
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdated, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
      },
    };
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© quand les filtres ou le tri sont modifiÃ©s
export class FiltersChangedEvent extends Event {
  /**
   * CrÃ©e un nouveau FiltersChangedEvent
   * @param {string} filter_params - Les paramÃ¨tres de filtre
   * @param {string} sorting_params - Les paramÃ¨tres de tri
   */
  constructor(filter_params, sorting_params) {
    super(ThemeEvents.filtersChanged, { bubbles: true });
    this.detail = {
      filter_params,
      sorting_params,
    };
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© aprÃ¨s la mise Ã  jour des filtres ou du tri
export class FiltersUpdatedEvent extends Event {
  // CrÃ©e un nouveau FiltersUpdatedEvent
  constructor() {
    super(ThemeEvents.filtersUpdated, { bubbles: true });
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© aprÃ¨s la mise Ã  jour des cross-sells Ã  activer
export class ToggleCrossSellsUpdatedEvent extends Event {
  // CrÃ©e un nouveau ToggleCrossSellsUpdatedEvent
  constructor() {
    super(ThemeEvents.toggleCrossSellsUpdated, { bubbles: true });
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© aprÃ¨s la mise Ã  jour du composant RÃ©ductions par quantitÃ©
export class QuantityBreaksUpdatedEvent extends Event {
  // CrÃ©e un nouveau QuantityBreaksUpdatedEvent
  constructor() {
    super(ThemeEvents.quantityBreaksUpdated, { bubbles: true });
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© aprÃ¨s la mise Ã  jour du composant Abonnement
export class SubscriptionUpdatedEvent extends Event {
  // CrÃ©e un nouveau SubscriptionUpdatedEvent
  constructor() {
    super(ThemeEvents.subscriptionUpdated, { bubbles: true });
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© aprÃ¨s la mise Ã  jour complÃ¨te du formulaire produit (prix calculÃ©s)
export class FormUpdatedEvent extends Event {
  /**
   * CrÃ©e un nouveau FormUpdatedEvent
   * @param {Object} prices - Les prix calculÃ©s du formulaire
   * @param {number} prices.price - Le prix total
   * @param {number} prices.compare_at_price - Le prix barrÃ© total
   * @param {number} prices.difference_price - La diffÃ©rence de prix
   */
  constructor(prices) {
    super(ThemeEvents.formUpdated, { bubbles: true });
    this.detail = { prices };
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© quand la wishlist est mise Ã  jour
export class WishlistUpdatedEvent extends Event {
  /**
   * CrÃ©e un nouveau WishlistUpdatedEvent
   * @param {Array} items - Le tableau des items de la wishlist
   * @param {number} count - Le nombre d'items dans la wishlist
   */
  constructor(items, count) {
    super(ThemeEvents.wishlistUpdated, { bubbles: true });
    this.detail = {
      items,
      count,
    };
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© quand un item est ajoutÃ© Ã  la wishlist
export class WishlistItemAddedEvent extends Event {
  /**
   * CrÃ©e un nouveau WishlistItemAddedEvent
   * @param {Object} item - L'item ajoutÃ© Ã  la wishlist
   * @param {string} item.productId - L'identifiant du produit
   * @param {string} item.title - Le titre du produit
   * @param {string} [item.handle] - Le handle du produit
   * @param {string} [item.url] - L'URL du produit
   * @param {string} [item.imageUrl] - L'URL de l'image du produit
   */
  constructor(item) {
    super(ThemeEvents.wishlistItemAdded, { bubbles: true });
    this.detail = {
      item,
    };
  }
}

// Ã‰vÃ©nement dÃ©clenchÃ© quand un item est retirÃ© de la wishlist
export class WishlistItemRemovedEvent extends Event {
  /**
   * CrÃ©e un nouveau WishlistItemRemovedEvent
   * @param {string} productId - L'identifiant du produit retirÃ©
   */
  constructor(productId) {
    super(ThemeEvents.wishlistItemRemoved, { bubbles: true });
    this.detail = {
      productId,
    };
  }
}

export default ThemeEvents;
